import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Search, Package, Truck, Check, Clock, MapPin, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Footer from "@/components/Footer";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_MAP = {
  pending_payment: { label: "Awaiting Payment", step: 0, color: "text-yellow-600" },
  paid: { label: "Payment Confirmed", step: 1, color: "text-blue-600" },
  processing: { label: "Being Prepared", step: 2, color: "text-indigo-600" },
  fulfilled: { label: "Shipped", step: 3, color: "text-green-600" },
  shipped: { label: "Shipped", step: 3, color: "text-green-600" },
  delivered: { label: "Delivered", step: 4, color: "text-green-700" },
  canceled: { label: "Canceled", step: -1, color: "text-red-600" },
};

const STEPS = [
  { icon: Check, label: "Confirmed" },
  { icon: Package, label: "Preparing" },
  { icon: Truck, label: "Shipped" },
  { icon: MapPin, label: "Delivered" },
];

const TrackOrder = () => {
  const [searchParams] = useSearchParams();
  const [orderId, setOrderId] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const idFromUrl = searchParams.get("id");
    if (idFromUrl) {
      setOrderId(idFromUrl);
      trackOrder(idFromUrl);
    }
  }, []);

  const trackOrder = async (id) => {
    const trackId = id || orderId.trim();
    if (!trackId) return;

    setLoading(true);
    setError("");
    setOrder(null);

    try {
      const res = await axios.get(`${API}/orders/track/${trackId}`);
      setOrder(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Order not found. Please check your order ID.");
    } finally {
      setLoading(false);
    }
  };

  const handleTrack = (e) => {
    e.preventDefault();
    trackOrder();
  };

  const statusInfo = order ? (STATUS_MAP[order.status] || STATUS_MAP[order.printful_status] || { label: order.status, step: 1, color: "text-gray-600" }) : null;
  const currentStep = statusInfo?.step ?? 0;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="px-6 md:px-12 lg:px-24 py-8 border-b border-gray-200">
        <div className="max-w-[1800px] mx-auto flex justify-between items-center">
          <Link to="/" className="font-['Syne'] font-extrabold text-2xl tracking-tight" data-testid="track-logo">
            CUE.PO
          </Link>
          <Link to="/" className="flex items-center gap-2 text-gray-500 hover:text-black transition-colors" data-testid="track-back">
            <ArrowLeft className="w-4 h-4" />
            Home
          </Link>
        </div>
      </header>

      <main className="flex-1 px-6 md:px-12 lg:px-24 py-16 md:py-24">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-3" data-testid="track-heading">TRACK YOUR ORDER</h1>
          <p className="text-gray-500 mb-10">Enter the order ID from your confirmation email.</p>

          {/* Search Form */}
          <form onSubmit={handleTrack} className="flex gap-3 mb-12" data-testid="track-form">
            <Input
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              placeholder="Paste your order ID here"
              className="flex-1 h-12 text-base"
              data-testid="track-input"
            />
            <Button
              type="submit"
              disabled={loading || !orderId.trim()}
              className="btn-primary h-12 px-8 flex items-center gap-2"
              data-testid="track-submit"
            >
              {loading ? (
                <span className="animate-pulse">Searching...</span>
              ) : (
                <>
                  <Search className="w-4 h-4" />
                  Track
                </>
              )}
            </Button>
          </form>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-sm p-5 text-red-700 mb-8" data-testid="track-error">
              {error}
            </div>
          )}

          {/* Order Results */}
          {order && (
            <div className="space-y-8 animate-fade-in-up" data-testid="track-results">
              {/* Status Badge */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 uppercase tracking-wider mb-1">Status</p>
                  <p className={`text-2xl font-bold ${statusInfo.color}`} data-testid="track-status">
                    {statusInfo.label}
                  </p>
                </div>
                {order.poster_image_url && (
                  <a
                    href={order.poster_image_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-[#002FA7] hover:underline flex items-center gap-1"
                    data-testid="track-poster-link"
                  >
                    View Poster <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>

              {/* Progress Steps */}
              {currentStep >= 0 && (
                <div className="relative" data-testid="track-progress">
                  <div className="flex justify-between items-center">
                    {STEPS.map((step, i) => {
                      const isActive = i < currentStep;
                      const isCurrent = i === currentStep - 1;
                      const Icon = step.icon;
                      return (
                        <div key={step.label} className="flex flex-col items-center relative z-10">
                          <div
                            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                              isActive || isCurrent
                                ? "bg-[#002FA7] text-white"
                                : "bg-gray-100 text-gray-400"
                            }`}
                          >
                            <Icon className="w-5 h-5" />
                          </div>
                          <span className={`text-xs mt-2 font-medium ${isActive || isCurrent ? "text-[#002FA7]" : "text-gray-400"}`}>
                            {step.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  {/* Progress bar */}
                  <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200 -z-0 mx-5">
                    <div
                      className="h-full bg-[#002FA7] transition-all duration-500"
                      style={{ width: `${Math.max(0, Math.min(100, ((currentStep - 1) / (STEPS.length - 1)) * 100))}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Tracking Info */}
              {order.tracking && order.tracking.tracking_number && (
                <div className="bg-blue-50 border border-blue-200 rounded-sm p-5" data-testid="track-shipping-info">
                  <h3 className="font-bold text-lg flex items-center gap-2 mb-3">
                    <Truck className="w-5 h-5 text-[#002FA7]" />
                    Shipping Details
                  </h3>
                  <div className="space-y-2 text-sm">
                    {order.tracking.carrier && (
                      <p><span className="text-gray-500">Carrier:</span> <span className="font-medium">{order.tracking.carrier}</span></p>
                    )}
                    <p><span className="text-gray-500">Tracking #:</span> <span className="font-medium font-mono">{order.tracking.tracking_number}</span></p>
                    {order.tracking.tracking_url && (
                      <a
                        href={order.tracking.tracking_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[#002FA7] hover:underline font-medium"
                        data-testid="track-tracking-url"
                      >
                        Track with carrier <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    {order.tracking.estimated_delivery && (
                      <p><span className="text-gray-500">Est. Delivery:</span> <span className="font-medium">{order.tracking.estimated_delivery}</span></p>
                    )}
                  </div>
                </div>
              )}

              {/* Order Details */}
              <div className="bg-gray-50 rounded-sm p-6" data-testid="track-order-details">
                <h3 className="font-bold text-lg mb-4">Order Details</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Poster</span>
                    <span className="font-medium">{order.product_name} ({order.product_dimensions})</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Your Goals</span>
                    <span className="font-medium text-right">
                      {order.goals.map((g, i) => (
                        <span key={i} className="block">{i + 1}. {g}</span>
                      ))}
                    </span>
                  </div>
                  <div className="border-t pt-3 mt-3 space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Poster Price</span>
                      <span>${order.product_price.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Shipping</span>
                      <span>{order.shipping_cost === 0 ? "FREE" : `$${order.shipping_cost.toFixed(2)}`}</span>
                    </div>
                    {order.discount_amount > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>Discount</span>
                        <span>-${order.discount_amount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-base pt-2 border-t">
                      <span>Total</span>
                      <span>${order.total_amount.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              {order.shipping_name && (
                <div className="text-sm" data-testid="track-address">
                  <h3 className="font-bold text-lg mb-2">Ships To</h3>
                  <p className="text-gray-600 leading-relaxed">
                    {order.shipping_name}<br />
                    {order.shipping_city}, {order.shipping_state} {order.shipping_zip}<br />
                    {order.shipping_country}
                  </p>
                </div>
              )}

              {/* Help */}
              <p className="text-sm text-gray-400 text-center pt-4">
                Need help? <Link to="/contact" className="text-[#002FA7] hover:underline">Contact us</Link>
              </p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TrackOrder;
